import React, { ButtonHTMLAttributes, HTMLProps, forwardRef } from 'react'

import { twMerge } from 'tailwind-merge'

import IconButton from '@/components/common/IconButtonV2'

export const TOOLBAR_BUTTON_CLASSNAME = 'rich-text-area-toolbar-button'

export type ToolbarWrapperProps = {
  shouldShowContent?: boolean
  isVertical?: boolean
} & HTMLProps<HTMLDivElement>

const ToolbarWrapper = forwardRef<HTMLDivElement, ToolbarWrapperProps>(
  (
    {
      shouldShowContent = true,
      children,
      isVertical = false,
      className,
      ...rest
    },
    ref,
  ) => {
    const toolbarClassName = twMerge(
      'bg-white dark:bg-primary-900 text-primary-900 dark:text-primary-100 inline-flex h-full leading-none gap-0.5',
      'rounded-md border border-primary-300 dark:border-primary-700',
      isVertical ? 'flex-col p-2' : 'flex-row p-1 items-center',
      className,
    )

    return (
      shouldShowContent && (
        <div className={toolbarClassName} {...rest} ref={ref}>
          {children}
        </div>
      )
    )
  },
)

ToolbarWrapper.displayName = 'Toolbar'

export type ToolbarDividerProps = {
  horizontal?: boolean
} & HTMLProps<HTMLDivElement>

const ToolbarDivider = forwardRef<HTMLDivElement, ToolbarDividerProps>(
  ({ horizontal, className, ...rest }, ref) => {
    const dividerClassName = twMerge(
      'bg-primary-200 dark:bg-primary-800',
      horizontal
        ? 'w-full min-w-[1.5rem] h-[1px] my-1 first:mt-0 last:mt-0'
        : 'h-full min-h-[1.5rem] w-[1px] mx-1 first:ml-0 last:mr-0',
      className,
    )

    return <div className={dividerClassName} ref={ref} {...rest} />
  },
)

ToolbarDivider.displayName = 'Toolbar.Divider'

export type ToolbarButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  isActive?: boolean
  tooltip?: React.ReactNode
}

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ children, className, tooltip, 'aria-label': ariaLabel, ...rest }, ref) => {
    return (
      <IconButton
        ref={ref}
        {...rest}
        aria-label={
          ariaLabel ?? (typeof tooltip === 'string' ? tooltip : undefined)
        }
        title={typeof tooltip === 'string' ? tooltip : undefined}
        className={twMerge(TOOLBAR_BUTTON_CLASSNAME, className)}
      >
        {children}
      </IconButton>
    )
  },
)

ToolbarButton.displayName = 'ToolbarButton'

export const Toolbar = {
  Wrapper: ToolbarWrapper,
  Divider: ToolbarDivider,
  Button: ToolbarButton,
}
